import {
  createContext,
  createEffect,
  createSignal,
  JSXElement,
  mergeProps,
  Show,
  untrack,
  useContext
} from 'solid-js';
import { createStore } from 'solid-js/store';
import UpgradeCustomizationAddonModal from '~/pages/Billing/UpgradeCustomizationAddonModal.tsx';
import {
  getEnvironmentUsedForBilling,
  getResourceContextDataForBilling,
  isBillingEnabled
} from '~/pages/Billing/utils.ts';
import { useAppData } from '~/components/AppData';
import { ResourceContextData } from '~/api/types/resourceContextData.ts';
import logger from '~/utils/logger';
import {
  getBillingAddonsQueryKey,
  getWorkspaceBillingInfo,
  retryAuthorizedRequests,
  setContexts
} from '~/api';
import { AddonData } from '~/api/types/billingData.ts';
import { createQuery } from '@tanstack/solid-query';
import {
  BillingProductUiIdentifiers,
  isAddonOfType
} from '~/pages/Billing/BillingUsage/utils.ts';

type WorkspaceBillingContextValueType = {
  shouldShowCustomizationUpgradeModal: boolean;
  showCustomizationUpgradeModal: () => void;
  hideCustomizationUpgradeModal: () => void;
  allowCustomization: () => boolean;
  enableCustomizationAddon: () => Promise<void>;
  isCustomizationAddonSubscribed: () => boolean;
  refetchBillingInfo?: () => Promise<void>;
  isPaymentMethodPresent: () => Promise<boolean>;
  getPublishableToken: () => string;
};

const defaultValue: WorkspaceBillingContextValueType = {
  shouldShowCustomizationUpgradeModal: false,
  showCustomizationUpgradeModal: () => {},
  hideCustomizationUpgradeModal: () => {},
  allowCustomization: () => false,
  enableCustomizationAddon: async () => {},
  isCustomizationAddonSubscribed: () => false,
  isPaymentMethodPresent: async () => false,
  getPublishableToken: () => ''
};

const WorkspaceBillingContext =
  createContext<WorkspaceBillingContextValueType>(defaultValue);

type WorkspaceBillingContextProviderProps = {
  children: JSXElement;
};

export default function WorkspaceBillingContextProvider(
  props: WorkspaceBillingContextProviderProps
): JSXElement {
  const merged = mergeProps({}, props);
  const appData = useAppData();

  const [billingResourceContext, setBillingResourceContext] =
    createSignal<ResourceContextData>({});

  const [subscribedAddons, setSubscribedAddons] = createSignal<AddonData[]>([]);

  const [contextValue, setContextValue] =
    createStore<WorkspaceBillingContextValueType>({
      ...defaultValue,
      showCustomizationUpgradeModal: () => {
        setContextValue('shouldShowCustomizationUpgradeModal', true);
      },
      hideCustomizationUpgradeModal: () => {
        setContextValue('shouldShowCustomizationUpgradeModal', false);
      },
      allowCustomization: () => {
        if (!isBillingEnabled()) {
          return true;
        }
        return (
          contextValue.isCustomizationAddonSubscribed() ||
          billingResourceContext().customizationAddonSubscribed !== undefined
        );
      },
      enableCustomizationAddon: async () => {
        try {
          const billingContextEnvironment = getEnvironmentUsedForBilling({
            appData
          });
          if (!billingContextEnvironment) {
            throw new Error(`Failed to select billing environment`);
          }
          await setContexts({
            environmentId: billingContextEnvironment.id,
            environmentDomain: billingContextEnvironment.domain,
            data: {
              ...billingResourceContext(),
              customizationAddonSubscribed: new Date().getTime()
            }
          });
          await fetchBillingResourceContext();
        } catch (e) {
          logger.error(
            `Error setting resource context while enabling customization addon`,
            e
          );
          throw e;
        }
      },
      isCustomizationAddonSubscribed: () => {
        return subscribedAddons().some(
          (addon) =>
            isAddonOfType(
              addon,
              BillingProductUiIdentifiers.CUSTOMIZATION_PRO
            ) && addon.enabled
        );
      },
      refetchBillingInfo: async () => {
        await billingInfoQuery.refetch();
      },
      isPaymentMethodPresent: async () => {
        if (appData.workspace?.extendedInfo?.paymentMethodPresent) {
          return true;
        }
        try {
          const billingInfo = await getWorkspaceBillingInfo();
          return !!billingInfo.paymentMethod?.id;
        } catch (e) {
          logger.error(
            `Error fetching billing info to check payment method`,
            e
          );
          return false;
        }
      },
      getPublishableToken: () => billingInfoQuery.data?.publishableToken ?? ''
    });

  const fetchBillingResourceContext = async () => {
    setBillingResourceContext(
      await getResourceContextDataForBilling({
        appData
      })
    );
  };

  (async () => {
    try {
      await fetchBillingResourceContext();
    } catch (e) {
      logger.error(`Failed to fetch billing resource context`, e);
    }
  })();

  const billingInfoQuery = createQuery(() => ({
    queryKey: getBillingAddonsQueryKey(),
    queryFn: async () => {
      return await getWorkspaceBillingInfo({
        showAddonsOnly: true
      });
    },
    retry: retryAuthorizedRequests
  }));

  createEffect(() => {
    if (billingInfoQuery.isSuccess && billingInfoQuery.data) {
      untrack(() => {
        setSubscribedAddons(billingInfoQuery.data.addons || []);
      });
    } else if (billingInfoQuery.isError && billingInfoQuery.error) {
      logger.error(
        `WorkspaceBillingContext :: Error fetching billing info. Can't decide list of adons subscribed ${billingInfoQuery.error}`
      );
    }
  });

  return (
    <WorkspaceBillingContext.Provider value={contextValue}>
      <Show when={isBillingEnabled()}>
        <UpgradeCustomizationAddonModal />
      </Show>

      {merged.children}
    </WorkspaceBillingContext.Provider>
  );
}

export const useWorkspaceBillingContext = () =>
  useContext(WorkspaceBillingContext);
